import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Spoiler,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconFlag,
  IconMapPin,
  IconMessage2Bolt,
  IconNotes,
  IconPackage,
  IconTruck,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useMobile } from "@/hooks/useMobile";
import { CAR_WEIGHT } from "@/utils/constants";
import { ORDER_FIELD_LABELS, REQUIRED_ORDER_FIELDS } from "@/utils/orderParseSchema";
import { parseKakaoOrder } from "@/utils/parseKakaoOrder";

// 좌표가 함께 와야 하는 필드들. 매칭 실패 시 폼에 적용하지 않고 직접 검색하게 안내한다
const PLACE_FIELDS = {
  start: ["startName", "startAddress", "startAddressDetail", "startPhoneNumber"],
  end: ["endName", "endAddress", "endAddressDetail", "endPhoneNumber"],
};

function dateToType(dateStr) {
  if (!dateStr) return "";
  const d = dayjs(dateStr).format("YYYY-MM-DD");
  if (d === dayjs().format("YYYY-MM-DD")) return "today";
  if (d === dayjs().add(1, "day").format("YYYY-MM-DD")) return "tomorrow";
  if (
    d ===
    dayjs()
      .add(8 - dayjs().day(), "day")
      .format("YYYY-MM-DD")
  )
    return "monday";
  return "etc";
}

function placeValues(prefix, order, place) {
  if (!place) return {};

  return {
    [`${prefix}Name`]: place.name || "",
    [`${prefix}Address`]: place.address || "",
    [`${prefix}AddressDetail`]: place.addressDetail || order[`${prefix}AddressDetail`] || "",
    [`${prefix}PhoneNumber`]: order[`${prefix}PhoneNumber`] || place.contact || "",
    [`${prefix}Lat`]: place.lat,
    [`${prefix}Lng`]: place.lng,
  };
}

function buildFormValues({ order }, matches) {
  const { start: startPlace, end: endPlace } = matches;

  const startType = dateToType(order.startDate);
  let endType = dateToType(order.endDate);
  // 당착은 당상일 때만 허용 (New 페이지 규칙)
  if (endType === "today" && startType !== "today") endType = "etc";

  // 화물 중량을 못 실는 톤수는 폼에서 선택 불가라 버린다
  const carWeightItem = CAR_WEIGHT.find((c) => c.label === order.carWeight);
  const carWeightOk = carWeightItem && Number(order.productWeight || 0) <= carWeightItem.ton;

  return {
    ...placeValues("start", order, startPlace),
    startType,
    startDate: order.startDate ? dayjs(order.startDate).toDate() : null,
    loadTime: order.loadTime || "",

    ...placeValues("end", order, endPlace),
    endType,
    endDate: order.endDate ? dayjs(order.endDate).toDate() : null,
    unloadTime: endType === "today" ? "" : order.unloadTime || "",

    productWeight: order.productWeight ?? "",
    productContent: order.productContent || "",
    memo: order.memo || "",

    carWeight: carWeightOk ? order.carWeight : "",
    carType: order.carType || "",
    loadType: order.loadType || "지게차",
    unloadType: order.unloadType || "지게차",

    isRoundTrip: order.isRoundTrip ?? false,
    isMixed: order.isMixed ?? false,
    isReceiptRequired: order.isReceiptRequired ?? false,
  };
}


// 검토 화면은 폼 입력 순서가 아니라 "장소 → 화물 → 차량" 묶음으로 보여준다.
// 한 줄씩 훑는 것보다 카드 단위로 원문과 대조하는 편이 빠르다
const FIELD_GROUPS = [
  {
    key: "start",
    title: "상차지",
    icon: IconMapPin,
    fields: ["startName", "startAddress", "startAddressDetail", "startPhoneNumber", "startDate", "loadTime"],
  },
  {
    key: "end",
    title: "하차지",
    icon: IconFlag,
    fields: ["endName", "endAddress", "endAddressDetail", "endPhoneNumber", "endDate", "unloadTime"],
  },
  {
    key: "product",
    title: "화물",
    icon: IconPackage,
    fields: ["productContent", "productWeight"],
  },
  {
    key: "car",
    title: "차량",
    icon: IconTruck,
    fields: ["carWeight", "carType", "loadType", "unloadType"],
  },
];

const OPTION_FIELDS = ["isRoundTrip", "isMixed", "isReceiptRequired"];

// 모바일에서는 하단 버튼을 화면 폭만큼 늘리고 스크롤을 따라다니게 한다
const growOnMobile = (theme) => ({ [theme.fn.smallerThan("sm")]: { flex: 1 } });

const stickyFooter = (theme) => ({
  [theme.fn.smallerThan("sm")]: {
    position: "sticky",
    bottom: 0,
    // 모달 본문 패딩을 상쇄해 화면 폭을 꽉 채운다
    margin: `0 -${theme.spacing.md}px -${theme.spacing.md}px`,
    padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
    paddingBottom: `calc(${theme.spacing.md}px + env(safe-area-inset-bottom))`,
    borderTop: `1px solid ${
      theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[3]
    }`,
    backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
    zIndex: 1,
  },
});

function formatValue(key, value) {
  if (value === null || value === undefined || value === "") return null;
  if (key === "startDate" || key === "endDate") return dayjs(value).format("M/D(dd)");
  if (key === "productWeight") return `${value}톤`;
  if (typeof value === "boolean") return value ? "예" : "아니오";
  return String(value);
}

function FieldRow({ label, value, status }) {
  return (
    <Group noWrap align="flex-start" spacing="xs" position="apart" py={3}>
      <Text
        size="xs"
        color="dimmed"
        sx={(theme) => ({
          flexShrink: 0,
          width: 88,
          [theme.fn.smallerThan("sm")]: { width: 66 },
        })}
      >
        {label}
      </Text>
      <Text
        size="sm"
        weight={status?.tone ? 600 : 400}
        color={!status?.tone && (status?.dim || !value) ? "dimmed" : undefined}
        sx={(theme) => ({
          flex: 1,
          wordBreak: "keep-all",
          // 라이트/다크에서 모두 읽히는 명도를 골라야 해서 색 이름 대신 shade를 직접 지정한다
          color:
            status?.tone && theme.fn.themeColor(status.tone, theme.colorScheme === "dark" ? 4 : 8),
        })}
      >
        {value || (status?.label === "누락" ? "없음" : "-")}
      </Text>
      {status && (
        <Badge size="xs" color={status.badge} variant="light" sx={{ flexShrink: 0 }}>
          {status.label}
        </Badge>
      )}
    </Group>
  );
}

export default function KakaoParseModal({ companyId, applyValues }) {
  const [opened, { open, close }] = useDisclosure();
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const isMobile = useMobile();
  const requestRef = useRef(null);
  useEffect(() => () => requestRef.current?.abort(), []);

  async function handleParse() {
    if (!text.trim()) return toast.error("배차 요청 내용을 붙여넣어주세요");

    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    try {
      const parsed = await parseKakaoOrder({
        text: text.trim(),
        companyId: Number(companyId),
        signal: controller.signal,
      });
      if (!controller.signal.aborted) setResult(parsed);
    } catch (e) {
      if (!controller.signal.aborted) {
        toast.error(e.response?.data?.error?.message || e.message || "분석에 실패했습니다");
      }
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        setLoading(false);
      }
    }
  }

  function handleApply() {
    applyValues(buildFormValues(result, matches));
    toast.success("분석 결과를 폼에 적용했습니다");
    handleClose();
  }

  function handleClose() {
    requestRef.current?.abort();
    requestRef.current = null;
    setLoading(false);
    setText("");
    setResult(null);
    close();
  }

  const order = result?.order || {};
  const inferred = result?.inferred || [];

  const matches = result?.matchedPlaces || { start: null, end: null };

  // 매칭 실패한 쪽은 폼에 안 들어가므로 "누락"이 아니라 "검색 필요"로 따로 안내한다
  const unmatched = result
    ? [
        !matches.start && { side: "start", label: "상차지" },
        !matches.end && { side: "end", label: "하차지" },
      ].filter(Boolean)
    : [];
  const searchNeeded = unmatched.flatMap((u) => PLACE_FIELDS[u.side]);

  // 배지만으로는 훑어보기 어려워 값 텍스트에도 같은 신호를 준다.
  // 검색 필요는 값이 폼에 들어가지 않으므로 흐리게 죽여서 "이건 안 쓰인다"를 드러낸다
  function fieldStatus(key) {
    if (searchNeeded.includes(key)) return { label: "검색", badge: "orange", dim: true };
    if (inferred.includes(key)) return { label: "확인", badge: "yellow", tone: "yellow" };
    if (formatValue(key, order[key]) === null && REQUIRED_ORDER_FIELDS.includes(key))
      return { label: "누락", badge: "red", tone: "red" };
    return null;
  }

  const allFields = Object.keys(ORDER_FIELD_LABELS);
  const counts = {
    search: unmatched.length,
    check: allFields.filter((k) => fieldStatus(k)?.label === "확인").length,
    missing: allFields.filter((k) => fieldStatus(k)?.label === "누락").length,
  };
  const activeOptions = OPTION_FIELDS.flatMap((key) => {
    if (key === "isMixed" && order[key] === false) return [{ key, label: "독차" }];
    if (order[key] === true) return [{ key, label: ORDER_FIELD_LABELS[key] }];
    return [];
  });

  const resultCards = (
    <Stack spacing="xs">
      {FIELD_GROUPS.map((group) => {
        const isPlace = group.key === "start" || group.key === "end";
        const matched = matches[group.key];
        return (
          <Paper key={group.key} withBorder p="sm" radius="sm">
            <Group spacing={6} mb={4}>
              <Group spacing={6} position="left">
                <group.icon size={16} />
                <Text size="sm" weight={600}>
                  {group.title}
                </Text>
              </Group>
              {isPlace && (
                <Badge size="xs" variant="light" color={matched ? "teal" : "orange"}>
                  {matched ? "등록된 장소" : "미등록"}
                </Badge>
              )}
            </Group>

            {group.fields.map((key) => (
              <FieldRow
                key={key}
                label={ORDER_FIELD_LABELS[key]}
                value={formatValue(key, order[key])}
                status={fieldStatus(key)}
              />
            ))}

            {isPlace && !matched && (
              <Text size="xs" color="orange" mt={6}>
                등록된 상/하차지에서 찾지 못해 좌표가 없습니다. 좌표가 없으면 거리·통행료를 계산할
                수 없어 폼에 넣지 않으니, 적용 후 {group.title} 검색으로 직접 지정해주세요.
              </Text>
            )}
          </Paper>
        );
      })}

      <Paper withBorder p="sm" radius="sm">
        <Group spacing={6} mb={6} position="left">
          <IconNotes size={16} />
          <Text size="sm" weight={600}>
            옵션 · 요청사항
          </Text>
        </Group>

        <Group spacing={6} position="left" mb={activeOptions.length ? 6 : 0}>
          {activeOptions.length === 0 ? (
            <Text size="sm" color="dimmed">
              선택된 옵션 없음
            </Text>
          ) : (
            activeOptions.map((option) => (
              <Badge key={option.key} size="sm" variant="light">
                {option.label}
              </Badge>
            ))
          )}
        </Group>

        <FieldRow
          label={ORDER_FIELD_LABELS.memo}
          value={formatValue("memo", order.memo)}
          status={fieldStatus("memo")}
        />
      </Paper>
    </Stack>
  );

  return (
    <>
      <Button
        onClick={open}
        disabled={!companyId}
        variant="light"
        leftIcon={<IconMessage2Bolt size={18} />}
      >
        카톡 요청 분석
      </Button>

      <Modal
        opened={opened}
        onClose={handleClose}
        title="카카오톡 배차 요청 분석"
        size={result ? 940 : "lg"}
        fullScreen={isMobile}
        radius={isMobile ? 0 : "md"}
        // fullScreen이어도 Mantine이 스크롤 영역을 90vh로 잡아 아래가 비므로 오프셋을 없앤다
        yOffset={isMobile ? 0 : "5vh"}
      >
        {!result && (
          <>
            <Textarea
              label="배차 요청 내용"
              description="카카오톡에서 받은 배차 요청을 그대로 붙여넣어주세요"
              placeholder={"내일 오전 9시 상차요\n안산 대한물산 → 평택항 CY\n파렛트 12개, 5톤 윙바디"}
              autosize
              minRows={6}
              maxRows={12}
              value={text}
              onChange={(e) => setText(e.currentTarget.value)}
            />

            <Group position="apart" mt="sm">
              <Text size="xs" color="dimmed">
                상/하차지 후보 자동 매칭 · 최근 이력 참고
              </Text>
              <Button onClick={handleParse} loading={loading}>
                분석
              </Button>
            </Group>
          </>
        )}

        {result && (
          <>
            <Group spacing={6} mb="sm">
              <Group spacing={6} position="left">
                {counts.missing > 0 && (
                  <Badge color="red" variant="filled">
                    누락 {counts.missing}
                  </Badge>
                )}
                {counts.search > 0 && (
                  <Badge color="orange" variant="filled">
                    검색 필요 {counts.search}
                  </Badge>
                )}
                {counts.check > 0 && (
                  <Badge color="yellow" variant="filled">
                    확인 필요 {counts.check}
                  </Badge>
                )}
                {counts.missing + counts.search + counts.check === 0 && (
                  <Badge color="teal" variant="filled">
                    확인할 항목 없음
                  </Badge>
                )}
              </Group>
              {!isMobile && (
                <Text size="xs" color="dimmed">
                  원문과 대조해 확인한 뒤 폼에 적용해주세요
                </Text>
              )}
            </Group>

            <Grid gutter="md">
              <Grid.Col span={12} sm={5}>
                <Text size="xs" color="dimmed" mb={4}>
                  원문
                </Text>
                <Paper withBorder p="sm" radius="sm">
                  {/* 모바일은 모달 본문 자체가 스크롤되므로 안쪽에 또 스크롤을 두지 않는다.
                      대신 원문을 접어두어 결과 카드가 첫 화면에 들어오게 한다 */}
                  {isMobile ? (
                    <Spoiler
                      maxHeight={66}
                      showLabel="원문 전체 보기"
                      hideLabel="원문 접기"
                      styles={{ control: { fontSize: 12 } }}
                    >
                      <Text size="sm" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                        {text}
                      </Text>
                    </Spoiler>
                  ) : (
                    <ScrollArea.Autosize mah={430}>
                      <Text size="sm" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                        {text}
                      </Text>
                    </ScrollArea.Autosize>
                  )}
                </Paper>
              </Grid.Col>

              <Grid.Col span={12} sm={7}>
                {isMobile ? (
                  resultCards
                ) : (
                  <ScrollArea.Autosize mah={460} offsetScrollbars>
                    {resultCards}
                  </ScrollArea.Autosize>
                )}
              </Grid.Col>
            </Grid>

            <Box mt="md" sx={stickyFooter}>
              <Flex gap="xs" justify="flex-end">
                <Button variant="default" onClick={() => setResult(null)} sx={growOnMobile}>
                  다시 입력
                </Button>
                <Button onClick={handleApply} sx={growOnMobile}>
                  폼에 적용
                </Button>
              </Flex>
            </Box>
          </>
        )}
      </Modal>
    </>
  );
}
